/**
 * Shared display formatters for the dispatcher web app (admin panel + tenant
 * pages).
 *
 * These consolidate formatter implementations that were previously duplicated
 * inline across AdminTenantsPage, AdminTenantDetailPage, AdminBillingPage,
 * AdminDashboardPage, AdminActivityLogPage, ProfilePage and BillingPage. Output
 * is intentionally locale-aware: every helper renders in the active i18n locale
 * so RU shows RU month/date names and 24h time, EN shows EN — never the raw
 * browser default (which produced US "M/D/YYYY, h:mm AM" mismatches). The
 * Belarus/EU currency formatting introduced by BUG-152..159 must not regress.
 */

import type { TFunction } from "i18next";

import i18n from "@/lib/i18n";
import { DEFAULT_VOLUME_UNIT, isIso4217Code } from "@/lib/units";

/**
 * Display options for the **instant** formatters ({@link formatDate},
 * {@link formatDayMonth}, {@link formatDateTime}, {@link formatTime}).
 *
 * ## Why these take a zone at all (TASK-5043)
 *
 * Two parallel formatting stacks used to exist: `lib/date.formatInTenantTz`
 * (zoned, ~14 call sites in routes/schedules) and this module (UNZONED,
 * imported by ~98 files). So everything outside routes/schedules rendered
 * instants in the **dispatcher's browser zone** — a dispatcher in Warsaw and
 * one in Minsk read different clock times off the same order, and neither
 * necessarily matched the tenant's own working day. `formatDateOnly` and
 * `formatDayMonthOnly` had already been hardened for the *date-only* half of
 * this family (BUG-355 / BUG-491); the *instant* half had not.
 *
 * ## The contract
 *
 * `timeZone` is **optional and un-defaulted on purpose.** Omitting it keeps the
 * browser zone, which is the honest rendering for an instant whose meaning IS
 * the viewer's own clock (a "last refreshed at" stamp) and for platform-wide
 * admin surfaces that span tenants in different zones. Supplying it — from
 * `useTenantTimezone()`, or from a `tenantTz` prop where no hook is reachable —
 * renders the tenant's wall clock. There is deliberately no implicit
 * `DEFAULT_TENANT_TZ` fallback here: silently assuming Minsk is how
 * `SessionsPanel` ended up "correct by coincidence".
 *
 * ## Use the right function for the value
 *
 * These are for **instants** (`...T12:34:56Z`). For a backend **date** field
 * (`YYYY-MM-DD`) use {@link formatDateOnly} / {@link formatDayMonthOnly}: a
 * bare date has no instant to convert, and pushing one through a zoned
 * formatter would shift it by a day near the boundary — the exact bug these
 * options exist to fix, reintroduced backwards.
 */
export interface FormatInstantOptions {
  /** BCP-47 locale; defaults to the active i18n language. */
  locale?: string;
  /** IANA timezone (e.g. "Europe/Minsk"); omitted → the browser's zone. */
  timeZone?: string;
}

/**
 * Normalizes the instant formatters' second parameter.
 *
 * Accepting `string | FormatInstantOptions` keeps the pre-existing
 * positional-`locale` call style working (dozens of call sites, and the
 * `formatPeriod`/`formatRelativeTime` convention) while letting a migrated
 * caller pass `{ timeZone }`. The `timeZone` key is returned as-is — `undefined`
 * included — because `Intl` treats an explicit `timeZone: undefined` exactly
 * like an absent one, so a caller that has no zone yet needs no special case.
 */
function instantOptions(arg?: string | FormatInstantOptions): {
  locale: string;
  timeZone?: string;
} {
  if (typeof arg === "string") return { locale: arg };
  return { locale: arg?.locale ?? i18n.language, timeZone: arg?.timeZone };
}

/**
 * Formats an ISO 8601 instant as a short date ("3 июн. 2026 г." / "Jun 3,
 * 2026").
 *
 * The locale defaults to the active i18n language so the output follows the
 * chosen UI language instead of the browser default. Pass a locale string, or a
 * {@link FormatInstantOptions} object to also pin the tenant's `timeZone`.
 */
export function formatDate(
  iso: string,
  options?: string | FormatInstantOptions,
): string {
  const { locale, timeZone } = instantOptions(options);
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone,
  });
}

/**
 * Formats a **date-only** ISO string (`"2026-09-01"`) as a short local date,
 * without the timezone shift {@link formatDate} would apply to it.
 *
 * `new Date("2026-09-01")` parses as UTC midnight, so in any negative-offset
 * timezone it renders as **Aug 31** — a whole day earlier. That is harmless for
 * a timestamp but not for a value that IS a date: a billing-period boundary
 * computed server-side in the tenant's timezone must render as the day the
 * server chose, or the UI disagrees with the invoice on exactly the boundary day
 * (the BUG-355 / BUG-491 family). Appending `T00:00:00` forces local-midnight
 * parsing, matching the existing audit-diff formatter (`diffLabels.ts`).
 *
 * Use this for API **date** fields (`YYYY-MM-DD`); use {@link formatDate} for
 * instants (`...T12:34:56Z`). A value that is not a bare date is passed through
 * to `formatDate` unchanged, so a caller cannot accidentally corrupt a timestamp.
 */
export function formatDateOnly(iso: string, locale?: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso.trim())
    ? new Date(`${iso.trim()}T00:00:00`).toLocaleDateString(
        locale ?? i18n.language,
        { year: "numeric", month: "short", day: "numeric" },
      )
    : formatDate(iso, locale);
}

/**
 * Formats an ISO 8601 date as a compact "month + day" label
 * ("Jun 3" / "3 июн."), used for chart axis/tooltip labels where the year is
 * implied by the surrounding period selector (TASK-3887).
 *
 * The locale defaults to the active i18n language. Pass an explicit `locale`
 * (e.g. the chart-theme `chartLocale(language)`) to keep grouping/format aligned
 * with the rest of a chart, or a {@link FormatInstantOptions} object to also pin
 * the tenant's `timeZone`.
 */
export function formatDayMonth(
  iso: string,
  options?: string | FormatInstantOptions,
): string {
  const { locale, timeZone } = instantOptions(options);
  return new Date(iso).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    timeZone,
  });
}

/**
 * "Month + day" for a **date-only** string, without the timezone shift
 * {@link formatDayMonth} applies to it — the same relationship
 * {@link formatDateOnly} has to {@link formatDate}.
 *
 * `new Date("2026-09-01")` parses as UTC midnight, so in any negative-offset
 * timezone `formatDayMonth` renders **Aug 31** for a delivery dated Sep 1. The
 * suite runs in UTC, where both parses agree, so no test can see it. Harmless
 * for an instant; wrong for a value that IS a date — the BUG-355 / BUG-491
 * family. Found by adversarial review of Sprint 184.
 *
 * A value that is not a bare `YYYY-MM-DD` is passed through unchanged, so a
 * caller cannot corrupt a timestamp with it.
 */
export function formatDayMonthOnly(iso: string, locale?: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso.trim())
    ? new Date(`${iso.trim()}T00:00:00`).toLocaleDateString(
        locale ?? i18n.language,
        { month: "short", day: "numeric" },
      )
    : formatDayMonth(iso, locale);
}

/**
 * A **date-only** value as «25 авг.» — but «25 авг. 2025 г.» when it falls
 * outside the current year.
 *
 * Written for the Routes list (Sprint 188), whose mockup shows «25 авг.» in the
 * ДАТА column and in the route label, against `formatDateOnly`'s
 * «25 авг. 2026 г.» — a year and a «г.» that are pure noise when every visible
 * row is from the year you are already looking at.
 *
 * **The year is dropped conditionally, never blindly.** A routes list is
 * routinely filtered back across a year boundary (the archive tab reaches years
 * back), and a bare «25 авг.» on a 2025 route sitting next to a 2026 one is not
 * merely terse — it is WRONG, and wrong in the direction that makes two
 * different days look like the same day. So the year is omitted only when it
 * matches the current one, which is exactly the case the mockup shows.
 *
 * `now` is injectable so the behaviour is testable without freezing the clock.
 */
export function formatDayMonthSmartYear(
  iso: string,
  locale?: string,
  now: Date = new Date(),
): string {
  const trimmed = iso.trim();
  const year = /^(\d{4})-\d{2}-\d{2}/.exec(trimmed)?.[1];
  return year !== undefined && Number(year) === now.getFullYear()
    ? formatDayMonthOnly(trimmed, locale)
    : formatDateOnly(trimmed, locale);
}

/**
 * Whether `locale` orders a date day-before-month (ru: `22 авг. 2026`) rather
 * than month-first (en-US: `Aug 22, 2026`).
 *
 * Asked of `Intl` itself via `formatToParts` instead of hardcoding a locale
 * list, so adding a language cannot silently get this wrong.
 */
function isDayBeforeMonth(locale: string): boolean {
  const parts = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).formatToParts(new Date("2026-08-22T00:00:00"));
  const day = parts.findIndex((p) => p.type === "day");
  const month = parts.findIndex((p) => p.type === "month");
  return day !== -1 && month !== -1 && day < month;
}

/**
 * Formats a **date-only** range as one compact label: `11 — 22 авг. 2026`.
 *
 * Written for the Orders toolbar's period chip (TASK-4823), where the mockup
 * shows the whole range in the width two separate date fields used to need.
 * Redundant parts are collapsed from the LEFT side, never the right, so the
 * label always ends in a fully-qualified date:
 *
 * | from | to | result |
 * |---|---|---|
 * | 2026-08-11 | 2026-08-22 | `11 — 22 авг. 2026` (same month and year) |
 * | 2026-07-30 | 2026-08-22 | `30 июл. — 22 авг. 2026` (same year) |
 * | 2025-12-30 | 2026-01-02 | `30 дек. 2025 — 2 янв. 2026` |
 * | 2026-08-11 | — | `с 11 авг. 2026` (open-ended) |
 * | — | 2026-08-22 | `по 22 авг. 2026` |
 *
 * Both bounds go through {@link formatDateOnly}, so a date-only string is never
 * shifted by a day in a negative-offset timezone (see its own docstring).
 *
 * `openFrom`/`openTo` are passed in already translated — this module holds no
 * i18n keys of its own.
 *
 * `dropCurrentYear` (opt-in, default OFF) additionally collapses the YEAR when
 * the whole range falls inside the current one, giving the Routes toolbar chip
 * its «18—25 авг.» (Sprint 188) instead of «18 — 25 авг. 2026 г.». It is opt-in
 * precisely so the Orders chip's existing output is untouched, and it is
 * CONDITIONAL for the same reason {@link formatDayMonthSmartYear} is: a range
 * that reaches into another year keeps its years, because dropping them there
 * would make two different years read identically.
 */
export function formatDateRangeCompact(
  from: string,
  to: string,
  labels: {
    openFrom: (date: string) => string;
    openTo: (date: string) => string;
  },
  locale?: string,
  options?: { dropCurrentYear?: boolean; now?: Date },
): string {
  const lang = locale ?? i18n.language;
  if (!from && !to) return "";

  // Whether the year may be omitted: opt-in AND every present bound is in the
  // current year. A range straddling New Year keeps both years.
  const currentYear = (options?.now ?? new Date()).getFullYear();
  const inCurrentYear = (iso: string) =>
    Number(iso.slice(0, 4)) === currentYear;
  const collapseYear =
    options?.dropCurrentYear === true &&
    (!from || inCurrentYear(from)) &&
    (!to || inCurrentYear(to));

  // The right-hand bound normally stays fully qualified; with the year
  // collapsed it becomes the same «25 авг.» short form the table cells use.
  const formatBound = (iso: string) =>
    collapseYear ? formatDayMonthOnly(iso, lang) : formatDateOnly(iso, lang);

  if (from && !to) return labels.openFrom(formatBound(from));
  if (!from && to) return labels.openTo(formatBound(to));

  const right = formatBound(to);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  const sameMonth = sameYear && from.slice(5, 7) === to.slice(5, 7);

  // Collapsing the left side only reads correctly when the locale puts the DAY
  // before the month («11 — 22 авг. 2026»). In a month-first locale it produces
  // nonsense — "11 — Aug 22, 2026" — so en-US keeps both sides whole.
  const dayFirst = isDayBeforeMonth(lang);

  const left =
    dayFirst && sameMonth
      ? new Date(`${from.trim()}T00:00:00`).toLocaleDateString(lang, {
          day: "numeric",
        })
      : sameYear
        ? formatDayMonth(`${from.trim()}T00:00:00`, lang)
        : formatBound(from);

  return `${left} — ${right}`;
}

/**
 * Formats an ISO 8601 instant as a short date + time
 * ("3 июн. 2026 г., 14:05" / "Jun 3, 2026, 14:05").
 *
 * The locale defaults to the active i18n language and the time is always
 * rendered in 24-hour form (`hour12: false`) for a consistent dispatcher UI.
 * Pass an explicit `locale`, or a {@link FormatInstantOptions} object to also
 * pin the tenant's `timeZone`.
 */
export function formatDateTime(
  iso: string,
  options?: string | FormatInstantOptions,
): string {
  const { locale, timeZone } = instantOptions(options);
  return new Date(iso).toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

/**
 * Formats an ISO 8601 instant as a short time-only label ("14:05").
 *
 * The locale defaults to the active i18n language and the time is always
 * rendered in 24-hour form (`hour12: false`) for a consistent dispatcher UI.
 * Pass an explicit `locale`, or a {@link FormatInstantOptions} object to also
 * pin the tenant's `timeZone`.
 */
export function formatTime(
  iso: string,
  options?: string | FormatInstantOptions,
): string {
  const { locale, timeZone } = instantOptions(options);
  return new Date(iso).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

/**
 * The calendar day (`YYYY-MM-DD`) an instant falls on in `timeZone`
 * (Sprint 222). `en-CA` is used only because its date format IS ISO order —
 * nothing locale-visible comes out of it.
 */
export function tenantDayKey(iso: string | Date, timeZone?: string): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(date);
}

/**
 * Moves a `YYYY-MM-DD` day key by `days` calendar days. Pure date arithmetic
 * in UTC, so a DST switch can never make "yesterday" equal "today" (which
 * subtracting 24 h from an instant can).
 */
export function shiftDayKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Options of {@link formatChatListTime}. */
export interface ChatListTimeOptions {
  /** The tenant's IANA zone — the day buckets are the tenant's days. */
  timeZone?: string;
  /** BCP-47 locale; defaults to the active i18n language. */
  locale?: string;
  /** "Now", injectable for tests. */
  now?: Date;
  /** Already-translated word for yesterday («вчера»); this module holds no keys. */
  yesterdayLabel: string;
}

/**
 * The chat thread list's smart time (Sprint 222, TASK-5126): today → `HH:MM`,
 * yesterday → «вчера», earlier this year → «3 окт.», an older year → with the
 * year («3 окт. 2025 г.»). All buckets are the TENANT's calendar days.
 */
export function formatChatListTime(
  iso: string,
  options: ChatListTimeOptions,
): string {
  const locale = options.locale ?? i18n.language;
  const { timeZone } = options;
  const today = tenantDayKey(options.now ?? new Date(), timeZone);
  const day = tenantDayKey(iso, timeZone);
  if (day === today) return formatTime(iso, { locale, timeZone });
  if (day === shiftDayKey(today, -1)) return options.yesterdayLabel;
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return new Date(iso).toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone,
  });
}

/**
 * Formats a billing period start date ("2026-06-01") as a localized
 * "Month Year" label (e.g. "June 2026" / "июнь 2026").
 *
 * Mirrors the previous `formatPeriod` on AdminBillingPage exactly: the date is
 * read at local midnight and the raw Intl output is returned (no capitalization
 * — the billing table never capitalized this value).
 *
 * **Deliberately takes no `timeZone` (TASK-5043).** `period_start` is a
 * `DateField` — a bare `YYYY-MM-DD` the backend already resolved in the
 * tenant's zone (`billing/services.tenant_period_bounds`). It denotes a MONTH,
 * not an instant, so there is nothing to convert: appending `T00:00:00` pins it
 * to local midnight, which is the same {@link formatDateOnly} guard and keeps
 * "2026-06-01" reading as June in every browser zone. Routing it through a
 * zoned instant formatter would reintroduce the day-shift this family of
 * helpers exists to prevent.
 */
export function formatPeriod(periodStart: string, locale: string): string {
  const date = new Date(periodStart + "T00:00:00");
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
}

/**
 * Formats a numeric amount as a localized currency string.
 *
 * Mirrors the previous per-row `Intl.NumberFormat` currency formatters on
 * AdminBillingPage. When `locale` is omitted the runtime default is used
 * (matching the KPI/stats formatters which used `"default"`); pass the active
 * i18n language for the per-record/table formatting that did so before.
 */
/**
 * Defensive parse for money strings coming from the billing API (Sprint 107
 * lesson). A missing or malformed value must never reach `formatCurrency` as
 * NaN — it would render as e.g. "BYNNaN". Falls back to 0 so cells always show
 * a clean formatted zero rather than the literal "NaN" (TASK-3712).
 */
export function parseMoney(value: string | null | undefined): number {
  const parsed = parseFloat(value ?? "0");
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Formats a plain number with locale-aware thousands grouping
 * (e.g. `28450 → "28 450"` in RU/EU locales, `"28,450"` in EN). Used by the
 * analytics tables for right-aligned numeric cells (TASK-3840).
 *
 * Non-finite values (NaN/Infinity) render the dash placeholder so cells never
 * leak "NaN". The locale defaults to the active i18n language so grouping
 * follows the chosen UI language.
 *
 * NOTE (TASK-3840): TASK-3835 introduced its own `formatNumber` in the chart
 * theme module. That module did not exist when this table work landed, so the
 * shared helper lives here in `lib/format` (the canonical formatter module). If
 * the chart-theme `formatNumber` later supersedes this, consolidate to one.
 */
export function formatNumber(
  value: number | null | undefined,
  locale?: string,
  dash = "—",
): string {
  if (value == null || !Number.isFinite(value)) {
    return dash;
  }
  return new Intl.NumberFormat(locale ?? i18n.language).format(value);
}

export function formatCurrency(
  amount: number,
  currency: string,
  locale?: string,
): string {
  // `currency` comes from the tenant's free-text `cost_unit` setting, which has
  // no backend `choices` — so it may be ANY string. `Intl` throws a RangeError
  // unless it is exactly three ASCII letters, which would crash every reports /
  // vehicles / order-detail screen for a tenant who typed "zł" (TASK-4756).
  // Fall back to rendering the label verbatim after the number rather than
  // substituting EUR: a wrong-but-familiar symbol is the exact lie this task
  // removes.
  if (!isIso4217Code(currency)) {
    const number = new Intl.NumberFormat(locale ?? "default", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return currency ? `${number} ${currency}` : number;
  }
  return new Intl.NumberFormat(locale ?? "default", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a **per-stop rate** — 4 decimals, currency **code** appended.
 *
 * `formatCurrency` above hard-clamps to 2 decimals, so it physically cannot
 * render a per-stop rate: the backend stores these as `DecimalField(
 * decimal_places=4)` and `0.0650` would come out as `0.07`. On a screen where
 * the rate is the headline number that is a money bug, not a display nit, which
 * is why `BillingPage.tsx` already builds its own 4-decimal formatter inline.
 * This is that formatter, shared (Sprint 180, TASK-4796).
 *
 * Use this for **rates**; use {@link formatCurrency} for totals/revenue/cost/
 * margin, where 2 decimals is the correct money presentation.
 *
 * The currency is appended as a code rather than rendered via `style:
 * "currency"` deliberately: `Intl`'s currency style re-imposes the currency's
 * own fraction digits, which would undo the 4-decimal precision. An empty
 * `currency` renders the bare number — the dealer currency is blank when it
 * inherits the platform default, and printing an empty label is better than
 * inventing a code the backend never agreed to.
 *
 * The locale defaults to the active i18n language so grouping/decimal
 * separators follow the chosen UI language.
 */
export function formatRatePerStop(
  amount: number,
  currency: string,
  locale?: string,
): string {
  const number = new Intl.NumberFormat(locale ?? i18n.language, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(amount);
  return currency ? `${number} ${currency}` : number;
}

/**
 * Returns the locale-resolved currency symbol for an ISO-4217 code
 * (e.g. `"EUR" → "€"`, `"USD" → "$"`, `"BYN" → "BYN"`, `"PLN" → "PLN"`).
 *
 * Used where only the symbol is needed without a numeric value — e.g. the
 * admin settings rate-field suffix and example, which must follow the
 * configured platform currency rather than hard-coding `€` (BUG-211). Derives
 * the symbol from `Intl.NumberFormat#formatToParts` so it stays consistent with
 * `formatCurrency` and never drifts from a manual symbol map. Falls back to the
 * raw code if the runtime cannot resolve a distinct symbol.
 */
export function getCurrencySymbol(currency: string, locale?: string): string {
  // Same free-text hazard as `formatCurrency`: a non-ISO `cost_unit` makes
  // `Intl` throw, so a code it cannot parse is returned verbatim (TASK-4756).
  if (!isIso4217Code(currency)) {
    return currency;
  }
  const part = new Intl.NumberFormat(locale ?? "default", {
    style: "currency",
    currency,
  })
    .formatToParts(0)
    .find((p) => p.type === "currency");
  return part?.value ?? currency;
}

/**
 * Formats a route distance in kilometres for display.
 *
 * Distinguishes an unknown distance (`null`/`undefined` — never computed) from a
 * genuine zero-distance route:
 * - `null` / `undefined` → the supplied dash placeholder (default "—").
 * - a real number (including `0`) → "<value> km" (e.g. "0 km", "12.5 km").
 *
 * The backend distance field is nullable on purpose (TASK-3621): `null` means
 * "not calculated", so we must never render a misleading "0.00 km" for it.
 */
export function formatDistanceKm(
  // `string | number`: mileage fields are DRF `DecimalField`s and arrive as
  // strings (`"18.59"`). The `Number(km)` below already handled that — only the
  // parameter type was pretending otherwise, forcing call sites to pre-cast.
  km: string | number | null | undefined,
  dash = "—",
): string {
  if (km == null) {
    return dash;
  }
  // Thousands grouping for long distances (TASK-3840) while preserving the
  // existing "<value> km" suffix and the null → dash distinction. The unit
  // suffix is localized via the `units` namespace (TASK-4439) so RU renders
  // "км" and EN "km".
  return `${formatNumber(Number(km), undefined, dash)} ${i18n.t("units.km")}`;
}

/**
 * Formats a duration given in whole minutes as a localized "Xh Ym" / "Ym"
 * label (e.g. `125 → "2ч 5мин"` under RU, `"2h 5m"` under EN). The unit
 * suffixes come from the `units` i18n namespace (TASK-4439); the hours part is
 * omitted when the duration is under one hour, matching the previous
 * per-file `${h}h ${m}m` implementations.
 *
 * `t` is passed in by the caller (from `useTranslation`) rather than importing
 * i18n here so the helper stays pure and testable. Null-handling (dash for a
 * missing value) is intentionally left to the call site, as the previous copies
 * differed on whether `0` renders as a dash or "0m".
 */
export function formatDuration(mins: number, t: TFunction): string {
  const total = Math.round(mins);
  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);
  return h > 0
    ? `${h}${t("units.h")} ${m}${t("units.min")}`
    : `${m}${t("units.min")}`;
}

/**
 * Renders the tenant's configured volume unit as a display label (TASK-4756).
 *
 * The stored value is free text (`TenantSettings.volume_unit`, default `m3`),
 * so an arbitrary string renders verbatim. The one special case is the default
 * `"m3"`, which is translated through the existing `units.m3` key so RU keeps
 * showing "м³" and EN "m³" instead of the raw ASCII `m3` that the backend
 * stores. Any other value — `"ft3"`, `"l"`, `"barrels"` — is shown exactly as
 * the tenant wrote it, because inventing a lookup that maps unknown strings
 * onto a known unit is how a UI ends up asserting the wrong unit.
 */
export function formatVolumeUnit(unit?: string | null): string {
  const value = (unit ?? "").trim() || DEFAULT_VOLUME_UNIT;
  return value === DEFAULT_VOLUME_UNIT ? i18n.t("units.m3") : value;
}

/**
 * Formats a volume figure as "<number> <unit>" using the tenant's configured
 * `volume_unit`.
 *
 * This is a LABEL change only — the value is never converted. Switching the
 * setting from `m3` to `ft3` relabels a stored `12.5` as "12.5 ft3"; it does
 * not multiply by 35.3. The stored figures are whatever the tenant entered, so
 * the setting is a declaration of what they already mean.
 *
 * Volume arrives from DRF `DecimalField` as a STRING (`"12.500"`), so input is
 * accepted as `unknown` and parsed defensively: a non-finite value renders the
 * `dash` placeholder rather than leaking `NaN` or the raw decimal string.
 */
export function formatVolume(
  value: unknown,
  unit?: string | null,
  options?: { locale?: string; dash?: string; fractionDigits?: number },
): string {
  const dash = options?.dash ?? "—";
  if (value === null || value === undefined || value === "") {
    return dash;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return dash;
  }
  const digits = options?.fractionDigits;
  const number = new Intl.NumberFormat(options?.locale ?? i18n.language, {
    ...(digits === undefined
      ? {}
      : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
  }).format(n);
  return `${number} ${formatVolumeUnit(unit)}`;
}
